import { z } from 'zod'
import {
  crmMessageProposalSchema,
  crmMessageReadSideEffectsSchema,
  crmMessageSummarySchema,
} from './crm-message-read'

export const CRM_EMAIL_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM email/transcript read alias. */
export const crmEmailReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  cursor: optionalQueryString,
  emailType: optionalQueryString,
  extractionStatus: optionalQueryString,
  matchStatus: optionalQueryString,
  companyId: optionalQueryString,
}).passthrough()

/** Email list reuses the full CRM message projection; the route only applies a channel filter. */
export const crmEmailListResponseSchema = z.object({
  contract_version: z.literal(CRM_EMAIL_READ_CONTRACT_VERSION),
  messages: z.array(crmMessageSummarySchema),
  nextCursor: z.string().nullable(),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const crmEmailParticipantRoleSchema = z.enum([
  'sender',
  'recipient',
  'cc',
  'mentioned',
  'forwarder',
])

export const crmEmailParticipantMatchStatusSchema = z.enum([
  'matched',
  'ambiguous',
  'unmatched',
  'shared',
  'self',
])

export const crmEmailParticipantSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  messageId: z.string().min(1),
  role: crmEmailParticipantRoleSchema,
  identifierEmail: z.string().nullable(),
  identifierName: z.string().nullable(),
  gPersonId: z.string().nullable(),
  gOrganizationId: z.string().nullable(),
  contactId: z.string().nullable(),
  companyId: z.string().nullable(),
  matchStatus: crmEmailParticipantMatchStatusSchema,
  matchRule: z.string().nullable(),
  matchScore: z.number().nullable(),
  resolvedByUserId: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const crmEmailDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_EMAIL_READ_CONTRACT_VERSION),
  message: crmMessageSummarySchema,
  participants: z.array(crmEmailParticipantSummarySchema),
  proposals: z.array(crmMessageProposalSchema),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const crmEmailParticipantsResponseSchema = z.object({
  contract_version: z.literal(CRM_EMAIL_READ_CONTRACT_VERSION),
  participants: z.array(crmEmailParticipantSummarySchema),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const crmTranscriptProviderSchema = z.enum(['zoom', 'fireflies', 'other'])
export const crmTranscriptSourceStatusSchema = z.enum(['fetched', 'not_ready', 'failed'])
export const crmTranscriptMatchStatusSchema = z.enum(['matched', 'unmatched', 'ambiguous'])
export const crmTranscriptExtractionStatusSchema = z.enum(['pending', 'extracted', 'failed', 'skipped'])

export const crmTranscriptParticipantSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
}).passthrough()

export const crmTranscriptSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  provider: crmTranscriptProviderSchema,
  connectionId: z.string().nullable(),
  externalCallId: z.string().min(1),
  companyId: z.string().nullable(),
  contactId: z.string().nullable(),
  title: z.string().nullable(),
  occurredAt: z.string().min(1),
  durationSeconds: z.number().nullable(),
  participants: z.array(crmTranscriptParticipantSchema),
  bodyPresent: z.boolean(),
  bodyRef: z.string().nullable(),
  sourceStatus: crmTranscriptSourceStatusSchema,
  matchStatus: crmTranscriptMatchStatusSchema,
  extractionStatus: crmTranscriptExtractionStatusSchema,
  extractionAttemptedAt: z.string().nullable(),
  interactionId: z.string().nullable(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const crmTranscriptListResponseSchema = z.object({
  contract_version: z.literal(CRM_EMAIL_READ_CONTRACT_VERSION),
  transcripts: z.array(crmTranscriptSummarySchema),
  nextCursor: z.string().nullable(),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const crmTranscriptDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_EMAIL_READ_CONTRACT_VERSION),
  transcript: crmTranscriptSummarySchema,
  proposals: z.array(crmMessageProposalSchema),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const CRM_EMAIL_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_EMAIL_DISABLED',
] as const

export const crmEmailReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_EMAIL_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmEmailReadQuery = z.infer<typeof crmEmailReadQuerySchema>
export type CrmEmailListResponse = z.infer<typeof crmEmailListResponseSchema>
export type CrmEmailParticipantRole = z.infer<typeof crmEmailParticipantRoleSchema>
export type CrmEmailParticipantMatchStatus = z.infer<typeof crmEmailParticipantMatchStatusSchema>
export type CrmEmailParticipantSummary = z.infer<typeof crmEmailParticipantSummarySchema>
export type CrmEmailDetailResponse = z.infer<typeof crmEmailDetailResponseSchema>
export type CrmEmailParticipantsResponse = z.infer<typeof crmEmailParticipantsResponseSchema>
export type CrmTranscriptProvider = z.infer<typeof crmTranscriptProviderSchema>
export type CrmTranscriptSourceStatus = z.infer<typeof crmTranscriptSourceStatusSchema>
export type CrmTranscriptMatchStatus = z.infer<typeof crmTranscriptMatchStatusSchema>
export type CrmTranscriptExtractionStatus = z.infer<typeof crmTranscriptExtractionStatusSchema>
export type CrmTranscriptParticipant = z.infer<typeof crmTranscriptParticipantSchema>
export type CrmTranscriptSummary = z.infer<typeof crmTranscriptSummarySchema>
export type CrmTranscriptListResponse = z.infer<typeof crmTranscriptListResponseSchema>
export type CrmTranscriptDetailResponse = z.infer<typeof crmTranscriptDetailResponseSchema>
export type CrmEmailReadErrorCode = (typeof CRM_EMAIL_READ_ERROR_CODES)[number]
export type CrmEmailReadErrorEnvelope = z.infer<typeof crmEmailReadErrorEnvelopeSchema>
